import type { I18n } from '@signbento/ui'
import { decideUpdate, isVersionMessage } from './app-version.js'
import { recordDiagnostic } from './diagnostics/store.js'
import { isOutsideShell } from './shell-state.js'
import { currentBuildId } from './sw-precache.js'

/**
 * The service worker on the page side (PLT-10, §11.8): registers the offline shell, announces a
 * new version once, and reloads only when the person chooses to. The rules themselves are in
 * `app-version.ts`; this is the DOM wiring.
 */

/** The registration, once there is one; `reloadToLatest` uses it to hand over to a new version. */
let registration: ServiceWorkerRegistration | null = null

/**
 * Reloads into the newest version: a version that is installed and waiting takes over first (the
 * worker only does so when asked), then the page reloads. Without a waiting version it is a plain
 * reload. Only ever called from a person's tap (the banner, the error screen's Reload).
 */
export function reloadToLatest(): void {
  const waiting = registration?.waiting ?? null
  if (waiting === null || !('serviceWorker' in navigator)) {
    window.location.reload()
    return
  }
  let reloaded = false
  const reload = () => {
    if (reloaded) return
    reloaded = true
    window.location.reload()
  }
  navigator.serviceWorker.addEventListener('controllerchange', reload)
  waiting.postMessage({ type: 'SKIP_WAITING' })
  // A worker that never takes over must not leave the button dead.
  window.setTimeout(reload, 3000)
}

export function registerAppServiceWorker(i18n: I18n): void {
  if (!('serviceWorker' in navigator)) return
  // A recipient's link installs nothing; a worker already installed by the sender app still
  // serves it.
  if (isOutsideShell(window.location.pathname)) return
  const build = currentBuildId(document)
  let announced = false
  const announce = (): void => {
    if (announced) return
    announced = true
    const banner = document.createElement('div')
    banner.className = 'app-version-banner'
    banner.setAttribute('role', 'status')
    banner.setAttribute('data-testid', 'app-version-banner')
    const text = document.createElement('span')
    text.textContent = i18n.t('common.new_version_available')
    const reload = document.createElement('button')
    reload.type = 'button'
    reload.className = 'op-button op-button--secondary'
    reload.textContent = i18n.t('common.reload')
    reload.addEventListener('click', reloadToLatest)
    banner.append(text, reload)
    document.body.append(banner)
  }

  window.addEventListener('load', () => {
    // A page that is already controlled by a worker is looking at a real update when a new one
    // arrives; the very first install is not an update.
    const hadController = navigator.serviceWorker.controller !== null
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (
        !isVersionMessage({
          origin: event.origin,
          expected_origin: window.location.origin,
          data: event.data,
        })
      ) {
        return
      }
      const decision = decideUpdate({
        had_controller: hadController,
        incoming_version: event.data.version,
        current_version: build,
      })
      if (decision.kind === 'ANNOUNCE') announce()
    })
    void navigator.serviceWorker
      .register('/sw.js')
      .then((registered) => {
        registration = registered
        // A version that finished installing: once it is ready to take over (not while it is
        // still downloading the new files), ask it which build it is. Its answer goes through the
        // version check above, so a page the network already served as that build (pages load
        // from the network first) isn't told a new version is available.
        const watch = (worker: ServiceWorker | null) => {
          if (worker === null) return
          const check = () => {
            if (worker.state !== 'installed') return
            worker.postMessage({ type: 'GET_SW_VERSION' })
          }
          check()
          worker.addEventListener('statechange', check)
        }
        watch(registered.waiting)
        registered.addEventListener('updatefound', () => watch(registered.installing))
      })
      // A registration that fails (a blocked or unsupported worker) is recorded as a diagnostic
      // rather than left as an unhandled rejection: the app keeps working online without it.
      .catch((error: unknown) => {
        recordDiagnostic({
          event_code: 'pwa.register_failed',
          severity: 'warn',
          context: { reason: error instanceof Error ? error.name : 'unknown' },
        })
      })
  })
}
